import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthAPI, MasterAPI } from '../api/api';
import './SignupPage.css';

// 비디오 배경 컴포넌트 - SignupPage 외부에 정의하여 리렌더링 시 재생성 방지
const VideoBackground = forwardRef((props, ref) => (
    <>
        <div className="bg-video">
            <video
                ref={ref}
                className="bg-video-el"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={`${process.env.PUBLIC_URL}/images/login-poster.jpg`}
            >
                <source src={`${process.env.PUBLIC_URL}/videos/login-bg.mp4`} type="video/mp4" />
            </video>
            <div className="bg-overlay" />
        </div>
        {/* 플로팅 배경 도형 */}
        <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
        </div>
    </>
));

function SignupPage() {
    const navigate = useNavigate();
    const videoRef = useRef(null);

    // 현재 단계: 1-이메일 입력, 2-인증코드 입력, 3-회원정보 입력, 4-완료
    const [step, setStep] = useState(1);

    // 마스터 데이터 상태
    const [affiliations, setAffiliations] = useState([]);
    const [headquarters, setHeadquarters] = useState([]);
    const [masterDataLoading, setMasterDataLoading] = useState(true);

    const [formData, setFormData] = useState({
        email: '',
        verificationCode: '',
        password: '',
        passwordConfirm: '',
        name: '',
        affiliationId: '',
        headquartersId: '',
        position: '',
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);

    // 인증코드 재발송 쿨다운
    const [resendCooldown, setResendCooldown] = useState(0);

    // iOS/Safari 자동재생 보정
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const tryPlay = () => v.play().catch(() => {});
        v.addEventListener('canplay', tryPlay, { once: true });
        return () => v.removeEventListener('canplay', tryPlay);
    }, []);

    // 마스터 데이터 로드 (컴포넌트 마운트 시)
    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                setMasterDataLoading(true);
                const [affiliationsRes, headquartersRes] = await Promise.all([
                    MasterAPI.getAffiliations(),
                    MasterAPI.getHeadquarters(),
                ]);
                setAffiliations(affiliationsRes.data || []);
                setHeadquarters(headquartersRes.data || []);
            } catch (error) {
                console.error('마스터 데이터 로드 실패:', error);
            } finally {
                setMasterDataLoading(false);
            }
        };

        fetchMasterData();
    }, []);

    // 쿨다운 타이머
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // 유효성 검사 함수들
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return '이메일을 입력해주세요.';
        if (!emailRegex.test(email)) return '올바른 이메일 형식이 아닙니다.';
        return '';
    };

    const validateVerificationCode = (code) => {
        if (!code) return '인증코드를 입력해주세요.';
        if (!/^\d{6}$/.test(code)) return '6자리 숫자를 입력해주세요.';
        return '';
    };

    const validatePassword = (password) => {
        if (!password) return '비밀번호를 입력해주세요.';
        if (password.length < 8) return '비밀번호는 8자 이상이어야 합니다.';
        if (password.length > 72) return '비밀번호는 72자 이하여야 합니다.';
        if (/[\x00-\x1F\x7F]/.test(password)) return '허용되지 않는 문자가 포함되어 있습니다.';
        if (!/[a-zA-Z]/.test(password)) return '영문자를 포함해야 합니다.';
        if (!/[0-9]/.test(password)) return '숫자를 포함해야 합니다.';
        if (!/[^A-Za-z0-9\s]/.test(password)) return '특수문자를 포함해야 합니다.';
        return '';
    };

    const validatePasswordConfirm = (password, passwordConfirm) => {
        if (!passwordConfirm) return '비밀번호 확인을 입력해주세요.';
        if (password !== passwordConfirm) return '비밀번호가 일치하지 않습니다.';
        return '';
    };

    const validateName = (name) => {
        if (!name) return '이름을 입력해주세요.';
        if (name.length < 2) return '이름은 2자 이상이어야 합니다.';
        return '';
    };

    const validateAffiliation = (affiliationId) => {
        if (!affiliationId) return '소속을 선택해주세요.';
        return '';
    };

    const validateHeadquarters = (headquartersId) => {
        if (!headquartersId) return '본부를 선택해주세요.';
        return '';
    };

    // 입력 필드 변경 핸들러
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // 실시간 유효성 검사
        let error = '';
        switch (name) {
            case 'email':
                error = validateEmail(value);
                break;
            case 'verificationCode':
                error = validateVerificationCode(value);
                break;
            case 'password':
                error = validatePassword(value);
                // 비밀번호 변경 시 확인 필드도 재검증
                if (formData.passwordConfirm) {
                    setErrors((prev) => ({
                        ...prev,
                        passwordConfirm: validatePasswordConfirm(value, formData.passwordConfirm),
                    }));
                }
                break;
            case 'passwordConfirm':
                error = validatePasswordConfirm(formData.password, value);
                break;
            case 'name':
                error = validateName(value);
                break;
            case 'affiliationId':
                error = validateAffiliation(value);
                break;
            case 'headquartersId':
                error = validateHeadquarters(value);
                break;
            default:
                break;
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // Step 1: 인증코드 발송
    const handleSendVerification = async (e) => {
        e.preventDefault();

        const emailError = validateEmail(formData.email);
        if (emailError) {
            setErrors({ email: emailError });
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            await AuthAPI.sendVerification(formData.email);
            setStep(2);
            setResendCooldown(60); // 60초 쿨다운
        } catch (error) {
            setErrors({ email: error.message || '인증코드 발송에 실패했습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Step 2: 인증코드 검증
    const handleVerifyCode = async (e) => {
        e.preventDefault();

        const codeError = validateVerificationCode(formData.verificationCode);
        if (codeError) {
            setErrors({ verificationCode: codeError });
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            const result = await AuthAPI.verifyEmail(formData.email, formData.verificationCode);
            if (result.verified) {
                setIsEmailVerified(true);
                setStep(3);
            }
        } catch (error) {
            setErrors({ verificationCode: error.message || '인증코드가 올바르지 않습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    // 인증코드 재발송
    const handleResendCode = async () => {
        if (resendCooldown > 0) return;

        setIsLoading(true);
        setErrors({});

        try {
            await AuthAPI.sendVerification(formData.email);
            setResendCooldown(60);
            setFormData((prev) => ({ ...prev, verificationCode: '' }));
        } catch (error) {
            setErrors({ verificationCode: error.message || '재발송에 실패했습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Step 3: 회원가입 완료
    const handleSubmit = async (e) => {
        e.preventDefault();

        // 전체 유효성 검사
        const newErrors = {
            password: validatePassword(formData.password),
            passwordConfirm: validatePasswordConfirm(formData.password, formData.passwordConfirm),
            name: validateName(formData.name),
            affiliationId: validateAffiliation(formData.affiliationId),
            headquartersId: validateHeadquarters(formData.headquartersId),
        };

        setErrors(newErrors);

        // 에러가 있으면 제출 중단
        if (Object.values(newErrors).some((error) => error)) {
            return;
        }

        setIsLoading(true);

        try {
            await AuthAPI.register({
                email: formData.email,
                password: formData.password,
                name: formData.name,
                affiliationId: formData.affiliationId,
                headquartersId: formData.headquartersId,
                position: formData.position || undefined,
            });

            setStep(4); // 완료 화면
        } catch (error) {
            setErrors({
                general: error.message || '회원가입 중 오류가 발생했습니다.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Step 4: 완료 화면
    if (step === 4) {
        return (
            <div className="signup-container">
                <VideoBackground ref={videoRef} />
                <div className="signup-box">
                    <h1 className="logo">MAP Group CIS</h1>
                    <div className="complete-message">
                        <div className="complete-icon">&#10003;</div>
                        <h2>회원가입 완료</h2>
                        <p>관리자 승인 후 로그인할 수 있습니다.</p>
                        <p className="complete-sub">승인이 완료되면 이메일로 안내드립니다.</p>
                    </div>
                    <Link to="/login" className="signup-button">
                        로그인 페이지로 이동
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="signup-container">
            <VideoBackground ref={videoRef} />

            <div className="signup-box">
                <h1 className="logo">MAP Group CIS</h1>
                <h2 className="signup-title">회원가입</h2>

                {/* 진행 단계 표시 */}
                <div className="step-indicator">
                    <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                        <span className="step-number">{step > 1 ? '✓' : '1'}</span>
                        <span className="step-label">이메일</span>
                    </div>
                    <div className="step-line"></div>
                    <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                        <span className="step-number">{step > 2 ? '✓' : '2'}</span>
                        <span className="step-label">인증</span>
                    </div>
                    <div className="step-line"></div>
                    <div className={`step ${step >= 3 ? 'active' : ''}`}>
                        <span className="step-number">3</span>
                        <span className="step-label">정보입력</span>
                    </div>
                </div>

                {errors.general && (
                    <div className="error-message general-error">{errors.general}</div>
                )}

                {/* Step 1: 이메일 입력 */}
                {step === 1 && (
                    <form onSubmit={handleSendVerification}>
                        <div className="input-group">
                            <label>이메일 <span className="required">*</span></label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="example@email.com"
                                className={errors.email ? 'input-error' : ''}
                                autoFocus
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                            <span className="input-hint">인증코드를 받을 이메일을 입력해주세요</span>
                        </div>

                        <button type="submit" className="signup-button" disabled={isLoading}>
                            {isLoading ? '발송 중...' : '인증코드 발송'}
                        </button>
                    </form>
                )}

                {/* Step 2: 인증코드 입력 */}
                {step === 2 && (
                    <form onSubmit={handleVerifyCode}>
                        <div className="verification-info">
                            <p><strong>{formData.email}</strong>로<br />인증코드를 발송했습니다.</p>
                        </div>

                        <div className="input-group">
                            <label>인증코드 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="verificationCode"
                                value={formData.verificationCode}
                                onChange={handleChange}
                                placeholder="6자리 숫자 입력"
                                className={errors.verificationCode ? 'input-error' : ''}
                                maxLength={6}
                                autoFocus
                            />
                            {errors.verificationCode && <span className="error-text">{errors.verificationCode}</span>}
                        </div>

                        <button type="submit" className="signup-button" disabled={isLoading}>
                            {isLoading ? '확인 중...' : '인증 확인'}
                        </button>

                        <div className="resend-section">
                            <span>인증코드가 오지 않았나요?</span>
                            <button
                                type="button"
                                className="resend-button"
                                onClick={handleResendCode}
                                disabled={resendCooldown > 0 || isLoading}
                            >
                                {resendCooldown > 0 ? `재발송 (${resendCooldown}초)` : '재발송'}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="back-button"
                            onClick={() => {
                                setStep(1);
                                setFormData((prev) => ({ ...prev, verificationCode: '' }));
                                setErrors({});
                            }}
                        >
                            이메일 변경
                        </button>
                    </form>
                )}

                {/* Step 3: 회원정보 입력 */}
                {step === 3 && (
                    <form onSubmit={handleSubmit}>
                        <div className="verified-email">
                            <span className="verified-icon">✓</span>
                            <span>{formData.email}</span>
                        </div>

                        <div className="input-group">
                            <label>비밀번호 <span className="required">*</span></label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="8자 이상, 영문+숫자+특수문자"
                                className={errors.password ? 'input-error' : ''}
                                autoFocus
                            />
                            {errors.password && <span className="error-text">{errors.password}</span>}
                        </div>

                        <div className="input-group">
                            <label>비밀번호 확인 <span className="required">*</span></label>
                            <input
                                type="password"
                                name="passwordConfirm"
                                value={formData.passwordConfirm}
                                onChange={handleChange}
                                placeholder="비밀번호를 다시 입력하세요"
                                className={errors.passwordConfirm ? 'input-error' : ''}
                            />
                            {errors.passwordConfirm && <span className="error-text">{errors.passwordConfirm}</span>}
                        </div>

                        <div className="input-group">
                            <label>이름 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="이름을 입력하세요"
                                className={errors.name ? 'input-error' : ''}
                            />
                            {errors.name && <span className="error-text">{errors.name}</span>}
                        </div>

                        <div className="input-row">
                            <div className="input-group half">
                                <label>소속 <span className="required">*</span></label>
                                <select
                                    name="affiliationId"
                                    value={formData.affiliationId}
                                    onChange={handleChange}
                                    disabled={masterDataLoading}
                                    className={errors.affiliationId ? 'input-error' : ''}
                                >
                                    <option value="">선택하세요</option>
                                    {affiliations.map((aff) => (
                                        <option key={aff.id} value={aff.id}>
                                            {aff.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.affiliationId && <span className="error-text">{errors.affiliationId}</span>}
                            </div>

                            <div className="input-group half">
                                <label>본부 <span className="required">*</span></label>
                                <select
                                    name="headquartersId"
                                    value={formData.headquartersId}
                                    onChange={handleChange}
                                    disabled={masterDataLoading}
                                    className={errors.headquartersId ? 'input-error' : ''}
                                >
                                    <option value="">선택하세요</option>
                                    {headquarters.map((hq) => (
                                        <option key={hq.id} value={hq.id}>
                                            {hq.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.headquartersId && <span className="error-text">{errors.headquartersId}</span>}
                            </div>
                        </div>

                        <div className="input-group">
                            <label>직급</label>
                            <input
                                type="text"
                                name="position"
                                value={formData.position}
                                onChange={handleChange}
                                placeholder="선택사항"
                            />
                        </div>

                        <button type="submit" className="signup-button" disabled={isLoading}>
                            {isLoading ? '처리 중...' : '회원가입'}
                        </button>
                    </form>
                )}

                <div className="signup-footer">
                    <span>이미 계정이 있으신가요?</span>
                    <Link to="/login" className="login-link">로그인</Link>
                </div>
            </div>
        </div>
    );
}

export default SignupPage;
